
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Device {
  id: string;
  provider_id: string;
  room_id?: string;
  device_name: string;
  device_id: string;
  device_pin?: string;
  seam_device_id?: string;
  is_active: boolean;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export const useDevices = (providerId?: string) => {
  return useQuery({
    queryKey: ['devices', providerId],
    queryFn: async () => {
      if (!providerId) {
        console.log('useDevices: No provider ID provided');
        return [];
      }
      
      console.log('useDevices: Fetching devices for provider:', providerId);
      
      const { data, error } = await supabase
        .from('devices')
        .select('*')
        .eq('provider_id', providerId)
        .eq('is_active', true)
        .order('device_name', { ascending: true });

      if (error) {
        console.error('useDevices: Error fetching devices:', error);
        throw error;
      }

      console.log('useDevices: Fetched devices:', data);
      console.log('useDevices: Devices with seam_device_id:', data?.filter(d => d.seam_device_id));
      console.log('useDevices: Devices without seam_device_id:', data?.filter(d => !d.seam_device_id));
      
      return data as Device[];
    },
    enabled: !!providerId,
  });
};

export const useCreateDevice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (device: Omit<Device, 'id' | 'created_at' | 'updated_at'>) => {
      console.log('useCreateDevice: Creating device with data:', device);
      
      // Validate required fields
      if (!device.provider_id) {
        throw new Error('Provider ID is required');
      }
      if (!device.room_id) {
        throw new Error('Room ID is required');
      }
      if (!device.device_name?.trim()) {
        throw new Error('Device name is required');
      }
      if (!device.device_id?.trim()) {
        throw new Error('Device ID is required');
      }

      try {
        console.log('useCreateDevice: Creating new device');
        
        const { data, error } = await supabase
          .from('devices')
          .insert({
            provider_id: device.provider_id,
            room_id: device.room_id,
            device_name: device.device_name,
            device_id: device.device_id,
            device_pin: device.device_pin || null,
            seam_device_id: device.seam_device_id || null,
            is_active: true,
            is_enabled: device.is_enabled !== undefined ? device.is_enabled : true
          })
          .select()
          .single();

        if (error) {
          console.error('useCreateDevice: Error creating device:', error);
          
          // Handle specific database errors
          if (error.code === '23505') {
            throw new Error('A device with this ID already exists. Please use a different Device ID.');
          } else if (error.code === '23503') {
            throw new Error('Invalid provider or room reference');
          } else {
            throw new Error(error.message || 'Failed to create device');
          }
        }

        console.log('useCreateDevice: Device created successfully:', data);
        return data;

      } catch (err) {
        console.error('useCreateDevice: Unexpected error:', err);
        if (err instanceof Error) {
          throw err;
        }
        throw new Error('Failed to create device');
      }
    },
    onSuccess: (data) => {
      console.log('useCreateDevice: Success callback, invalidating queries for provider:', data.provider_id);
      queryClient.invalidateQueries({ queryKey: ['devices', data.provider_id] });
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      toast({
        title: "Device Added Successfully",
        description: `${data.device_name} has been added to the location.`,
      });
    },
    onError: (error) => {
      console.error('useCreateDevice: Mutation error:', error);
      toast({
        title: "Failed to Add Device",
        description: error.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateDevice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Device> & { id: string }) => {
      const { data, error } = await supabase
        .from('devices')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['devices', data.provider_id] });
      const statusMessage = data.is_enabled ? 'enabled' : 'disabled';
      toast({
        title: "Device Updated",
        description: `${data.device_name} has been ${statusMessage} successfully.`,
      });
    },
    onError: (error) => {
      console.error('Error updating device:', error);
      toast({
        title: "Error",
        description: "Failed to update device. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteDevice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      // Instead of soft delete, completely remove the device to allow reuse of device_id
      const { error } = await supabase
        .from('devices')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return id;
    },
    onSuccess: (_, deviceId) => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      toast({
        title: "Device Removed",
        description: "Device has been removed successfully and can now be added to other locations.",
      });
    },
    onError: (error) => {
      console.error('Error deleting device:', error);
      toast({
        title: "Error",
        description: "Failed to remove device. Please try again.",
        variant: "destructive",
      });
    },
  });
};
